import PropTypes from "prop-types";
import { formatCurrency, validateThresholdValue } from "../../utils/utils";
import "./AccountsTable.css";

/**
 * ThresholdEditCell component - handles display and editing of threshold values
 * Separates threshold editing concerns from the main table component
 */
const ThresholdEditCell = ({
  isEditing,
  editValue,
  saving,
  threshold,
  currency,
  index,
  onEdit,
  onSave,
  onCancel,
  onChange,
}) => {
  // Validate current edit value for inline feedback
  const validation = isEditing ? validateThresholdValue(editValue) : { valid: true, error: null };
  const handleInputClick = (e) => {
    e.stopPropagation(); // Prevent row click when clicking on input
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !saving) {
      onSave(e);
    } else if (e.key === "Escape") {
      onCancel(e);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEdit(e);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit(e);
    }
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    onSave(e);
  };

  const handleCancelClick = (e) => {
    e.stopPropagation();
    onCancel(e);
  };

  const handleInputChange = (e) => {
    e.stopPropagation();
    onChange(e.target.value);
  };

  if (isEditing) {
    return (
      <div className="threshold-edit-wrapper" role="group" aria-label="Edit threshold">
        <div className="threshold-input-container">
          <input
            type="number"
            className={`threshold-edit-input ${!validation.valid ? "threshold-edit-input--error" : ""}`}
            value={editValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onKeyDown={handleInputKeyDown}
            disabled={saving}
            autoFocus
            step="0.01"
            min="0"
            aria-label={`Edit threshold for ${currency}`}
            aria-describedby={`threshold-hint-${index} ${!validation.valid ? `threshold-error-${index}` : ""}`}
            aria-invalid={!validation.valid}
          />
          {!validation.valid && (
            <span
              id={`threshold-error-${index}`}
              className="threshold-error-message"
              role="alert"
            >
              {validation.error}
            </span>
          )}
        </div>
        <span id={`threshold-hint-${index}`} className="sr-only">
          Press Enter to save, Escape to cancel
        </span>
        <button
          className="threshold-edit-save"
          onClick={handleSaveClick}
          disabled={saving || !validation.valid}
          title="Save threshold"
          aria-label="Save threshold"
        >
          {saving ? "..." : "✓"}
        </button>
        <button
          className="threshold-edit-cancel"
          onClick={handleCancelClick}
          disabled={saving}
          title="Cancel editing"
          aria-label="Cancel editing"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="threshold-value">
      <span
        className="threshold-amount"
        aria-label={`Threshold: ${formatCurrency(threshold, currency)}`}
      >
        {formatCurrency(threshold, currency)}
      </span>
      <button
        className="threshold-edit-btn"
        onClick={handleEditClick}
        onKeyDown={handleEditKeyDown}
        title="Edit threshold"
        aria-label={`Edit threshold for ${currency}`}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M11.707 1.293a1 1 0 00-1.414 0L9 2.586 11.414 5l1.293-1.293a1 1 0 000-1.414l-1-1zM10 6.414L7.586 4 2 9.586V12h2.414L10 6.414z" />
        </svg>
      </button>
    </div>
  );
};

ThresholdEditCell.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  editValue: PropTypes.string.isRequired,
  saving: PropTypes.bool.isRequired,
  threshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currency: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ThresholdEditCell;
